/**
 * Advance overlay Campaign/Frame lines between worker timing samples.
 * Comlink + main-thread stalls can space `getPlaybackTiming` replies by multiple seconds;
 * without local extrapolation `formatMs` jumps (skips 2+ displayed seconds) even though
 * the scheduler clock is fine.
 *
 * Stall honesty: freeze when the stuck badge is suspected (raw worker flatline). Do not
 * hard-cap paint-ahead — a 1.5s cap froze the overlay on low-end Android while polls were
 * still slower than that, which looked like "timers not ticking in real time".
 */
export function extrapolatePlaybackTimingOverlay(input: {
  frameElapsedMs: number
  frameDurationMs: number
  playlistElapsedMs: number
  playlistDurationMs: number
  sampleAtPerfMs: number
  nowPerfMs: number
  /** Optional ceil on wall delta (tests / callers that want a bound). */
  maxExtrapolateMs?: number
  /** Hold the last sample (e.g. stuck badge suspected / known flat worker clock). */
  freeze?: boolean
}): {
  frameElapsedMs: number
  playlistElapsedMs: number
} {
  const frameBase = Math.max(0, input.frameElapsedMs)
  const playlistBase = Math.max(0, input.playlistElapsedMs)
  if (input.freeze) {
    return {
      frameElapsedMs: frameBase,
      playlistElapsedMs: playlistBase
    }
  }
  const rawDelta = Math.max(0, input.nowPerfMs - input.sampleAtPerfMs)
  const deltaMs = input.maxExtrapolateMs == null
    ? rawDelta
    : Math.min(rawDelta, Math.max(0, input.maxExtrapolateMs))
  const frameCap = Math.max(0, input.frameDurationMs)
  const playlistCap = Math.max(0, input.playlistDurationMs)
  const frameElapsedMs = Math.min(frameCap, frameBase + deltaMs)
  /** Campaign only advances as far as the on-air frame did (segment boundary). */
  const frameAdvanceMs = frameElapsedMs - frameBase
  return {
    frameElapsedMs,
    playlistElapsedMs: Math.min(playlistCap, playlistBase + frameAdvanceMs)
  }
}
